import React, { useState, useEffect } from 'react';
import {
  Users,
  Building2,
  MapPin,
  FileText,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Sprout,
  Layers
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import KMLMapViewer from '../../components/common/KMLMapViewer';

/**
 * Admin Dashboard Component
 * 
 * Main dashboard providing system overview, statistics, and key metrics
 * for the Farm Monitor admin panel.
 */
const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    users: { total: 0, active: 0, new: 0 },
    watersheds: { total: 0, active: 0, new: 0 },
    sites: { total: 0, active: 0, new: 0 },
    points: { total: 0, active: 0, new: 0 },
    submissions: { total: 0, pending: 0, resolved: 0, critical: 0 },
  });
  const [chartData, setChartData] = useState({
    monthly: [],
    statusDistribution: [],
    categoryDistribution: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  console.log('Dashboard component mounting/rendering...');

  useEffect(() => {
    console.log('Dashboard useEffect triggered');
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('Starting dashboard data load...');
      console.log('Auth token:', apiService.getAuthToken() ? 'Present' : 'Missing');

      // Load dashboard statistics
      const response = await apiService.getDashboardStats();

      console.log('Dashboard stats response:', response);
      
      if (response && response.success && response.data && response.data.stats) {
        console.log('Setting stats:', response.data.stats);
        console.log('Setting charts:', response.data.charts);
        console.log('Beds data from API:', response.data.stats.beds);

        // Ensure all required properties exist with defaults (River Monitor)
        const statsData = {
          users: response.data.stats.users || { total: 0, active: 0, new: 0 },
          watersheds: response.data.stats.watersheds || { total: 0, active: 0, new: 0 },
          sites: response.data.stats.sites || { total: 0, active: 0, new: 0 },
          points: response.data.stats.points || { total: 0, active: 0, new: 0 },
          submissions: response.data.stats.submissions || { total: 0, pending: 0, resolved: 0, critical: 0 }
        };

        console.log('Processed statsData:', statsData);
        console.log('Beds in statsData:', statsData.beds);

        const chartsData = {
          monthly: response.data.charts?.monthly || [],
          statusDistribution: response.data.charts?.statusDistribution || [],
          categoryDistribution: response.data.charts?.categoryDistribution || []
        };

        setStats(statsData);
        setChartData(chartsData);
        console.log('Successfully loaded dashboard data from API');
        console.log('Stats state after setting:', statsData);
      } else {
        console.log('Dashboard API failed or returned invalid data, falling back...');
        console.log('Response:', response);
        setError('Dashboard API returned invalid data, using fallback data');
        await loadFallbackData();
      }
    } catch (error) {
      console.error('Dashboard loading error:', error);
      setError(`Dashboard API error: ${error.message || 'Unknown error'}`);
      // Try fallback approach
      await loadFallbackData();
    } finally {
      setLoading(false);
    }
  };

  const loadFallbackData = async () => {
    try {
      console.log('Loading fallback data...');
      const [usersRes, watershedsRes, sitesRes, pointsRes, submissionsRes] = await Promise.allSettled([
        apiService.getUsers({ limit: 1000 }),
        apiService.getWatersheds({ limit: 1000 }),
        apiService.getSites({ limit: 1000 }),
        apiService.getPoints({ limit: 1000 }),
        apiService.getSubmissions({ limit: 1000 })
      ]);

      console.log('Fallback responses:', { usersRes: usersRes.status, watershedsRes: watershedsRes.status, sitesRes: sitesRes.status, pointsRes: pointsRes.status, submissionsRes: submissionsRes.status });

      // Process users data
      const users = usersRes.status === 'fulfilled' && usersRes.value.success ? usersRes.value.data.users || [] : [];
      const activeUsers = users.filter(user => {
        const lastLogin = new Date(user.last_login_at);
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        return lastLogin > thirtyDaysAgo;
      });
      const newUsers = users.filter(user => {
        const created = new Date(user.created_at);
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return created > sevenDaysAgo;
      });

      // Process watersheds data
      const watersheds = watershedsRes.status === 'fulfilled' && watershedsRes.value.success ? watershedsRes.value.data.watersheds || [] : [];
      const newWatersheds = watersheds.filter(watershed => {
        const created = new Date(watershed.created_at);
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return created > sevenDaysAgo;
      });

      // Process sites data
      const sites = sitesRes.status === 'fulfilled' && sitesRes.value.success ? sitesRes.value.data.sites || [] : [];
      const newSites = sites.filter(site => {
        const created = new Date(site.created_at);
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return created > sevenDaysAgo;
      });

      // Process points data
      const points = pointsRes.status === 'fulfilled' && pointsRes.value.success ? pointsRes.value.data.points || [] : [];
      console.log('Fallback points data:', points);
      const newPoints = points.filter(point => {
        const created = new Date(point.created_at);
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return created > sevenDaysAgo;
      });

      // Process submissions data
      const submissions = submissionsRes.status === 'fulfilled' && submissionsRes.value.success ? submissionsRes.value.data.submissions || [] : [];
      const pendingSubmissions = submissions.filter(s => s.status === 'open' || s.status === 'in_progress');
      const resolvedSubmissions = submissions.filter(s => s.status === 'resolved' || s.status === 'closed');
      const criticalSubmissions = submissions.filter(s => s.severity === 'critical');

      // Set fallback stats
      const fallbackStats = {
        users: {
          total: users.length,
          active: activeUsers.length,
          new: newUsers.length
        },
        watersheds: {
          total: watersheds.length,
          active: watersheds.length,
          new: newWatersheds.length
        },
        sites: {
          total: sites.length,
          active: sites.length,
          new: newSites.length
        },
        points: {
          total: points.length,
          active: points.length,
          new: newPoints.length
        },
        submissions: {
          total: submissions.length,
          pending: pendingSubmissions.length,
          resolved: resolvedSubmissions.length,
          critical: criticalSubmissions.length
        },
      };

      console.log('Fallback stats being set:', fallbackStats);
      console.log('Fallback beds stats:', fallbackStats.beds);
      setStats(fallbackStats);

      // Generate simple chart data
      const statusDistribution = [
        { name: 'Open', value: submissions.filter(s => s.status === 'open').length, color: '#ef4444' },
        { name: 'In Progress', value: submissions.filter(s => s.status === 'in_progress').length, color: '#f59e0b' },
        { name: 'Resolved', value: submissions.filter(s => s.status === 'resolved').length, color: '#10b981' },
        { name: 'Closed', value: submissions.filter(s => s.status === 'closed').length, color: '#6b7280' },
      ];

      const categoryDistribution = [
        { name: 'Farm', value: submissions.filter(s => s.category === 'Farm').length },
        { name: 'Plot', value: submissions.filter(s => s.category === 'Plot').length },
        { name: 'Plant', value: submissions.filter(s => s.category === 'Plant').length },
        { name: 'Other', value: submissions.filter(s => !s.category || !['Farm', 'Plot', 'Plant'].includes(s.category)).length },
      ];

      setChartData({
        monthly: [], // Would need more complex processing for monthly data
        statusDistribution,
        categoryDistribution,
      });

    } catch (fallbackError) {
      console.error('Fallback data loading failed:', fallbackError);
      setError('Failed to load dashboard data');
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'primary', onClick }) => (
    <div
      className={`bg-white rounded-lg shadow p-6 ${onClick ? 'cursor-pointer hover:shadow-lg transition-shadow duration-200' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-secondary-600">{title}</p>
          <p className="text-2xl font-bold text-secondary-900">{value.toLocaleString()}</p>
          {subtitle && <p className="text-sm text-secondary-500">{subtitle}</p>}
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trend.positive ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp className={`w-4 h-4 mr-1 ${trend.positive ? '' : 'rotate-180'}`} />
            {trend.value}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-secondary-200 rounded-lg"></div>
                <div className="ml-4 flex-1">
                  <div className="h-4 bg-secondary-200 rounded w-24 mb-2"></div>
                  <div className="h-6 bg-secondary-200 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Dashboard</h1>
          <p className="text-secondary-600">Overview of your river monitoring system</p>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:grid-cols-5">
        <StatCard
          title="Total Watersheds"
          value={stats.watersheds.total}
          subtitle={`${stats.watersheds.new} new this week`}
          icon={Building2}
          trend={stats.watersheds.new > 0 ? { positive: true, value: `+${stats.watersheds.new} this week` } : null}
          color="green"
          onClick={() => navigate('/watersheds')}
        />
        <StatCard
          title="Total Sites"
          value={stats.sites.total}
          subtitle={`${stats.sites.new} new this week`}
          icon={MapPin}
          trend={stats.sites.new > 0 ? { positive: true, value: `+${stats.sites.new} this week` } : null}
          color="indigo"
          onClick={() => navigate('/sites')}
        />
        <StatCard
          title="Total Points"
          value={stats.points.total}
          subtitle={`${stats.points.active} active`}
          icon={Layers}
          trend={stats.points.new > 0 ? { positive: true, value: `+${stats.points.new} this week` } : null}
          color="purple"
          onClick={() => navigate('/points')}
        />
        <StatCard
          title="Total Submissions"
          value={stats.submissions.total}
          subtitle={`${stats.submissions.pending} pending`}
          icon={FileText}
          trend={stats.submissions.resolved > 0 ? { positive: true, value: `${stats.submissions.resolved} resolved` } : null}
          color="orange"
          onClick={() => navigate('/submissions')}
        />
        <StatCard
          title="Total Users"
          value={stats.users.total}
          subtitle={`${stats.users.active} active`}
          icon={Users}
          trend={stats.users.new > 0 ? { positive: true, value: `+${stats.users.new} this week` } : null}
          color="blue"
          onClick={() => navigate('/users')}
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Critical Issues */}
        {stats.submissions.critical > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
              <h3 className="text-lg font-semibold text-red-900">Critical Issues</h3>
            </div>
            <p className="text-red-700 mb-4">
              {stats.submissions.critical} critical issues need immediate attention
            </p>
            <button className="btn btn-danger">View Critical Issues</button>
          </div>
        )}

        {/* Recent Activity - Hidden for watershed_manager
        {user?.role !== 'watershed_manager' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                <div>
                  <p className="text-sm font-medium">New farm registered</p>
                  <p className="text-xs text-secondary-500">2 hours ago</p>
                </div>
              </div>
              <div className="flex items-center">
                <Clock className="w-5 h-5 text-yellow-500 mr-3" />
                <div>
                  <p className="text-sm font-medium">Issue reported</p>
                  <p className="text-xs text-secondary-500">4 hours ago</p>
                </div>
              </div>
              <div className="flex items-center">
                <Users className="w-5 h-5 text-blue-500 mr-3" />
                <div>
                  <p className="text-sm font-medium">New user registered</p>
                  <p className="text-xs text-secondary-500">1 day ago</p>
                </div>
              </div>
            </div>
          </div>
        )} */}

        {/* System Health */}
         {/* <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">System Health</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary-600">Database</span>
              <span className="flex items-center text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                Online
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary-600">API Service</span>
              <span className="flex items-center text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                Healthy
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary-600">Storage</span>
              <span className="flex items-center text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                Available
              </span>
            </div>
          </div>
        </div> */}
      </div>

      {/* KML Map Section */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6 relative z-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base md:text-lg font-semibold text-secondary-900">Watershed Monitoring Points</h3>
            <p className="text-xs md:text-sm text-secondary-600">Patapsco River Watershed (Middle Branch Segment)</p>
            <p className="text-xs md:text-sm text-secondary-600">Patapsco River - 1400 Meter Stretch (Old Ellicott City Bypass)</p>
          </div>
        </div>
        <div className="relative z-0 overflow-hidden rounded-lg">
          <KMLMapViewer
            kmlPath="/points.kml"
            height={500}
            center={[39.26, -76.79]}
            zoom={12}
          />
        </div>
      </div>

      {/* Charts */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"> */}
        {/* Submission Status Distribution
        {chartData.statusDistribution.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Submission Status</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.statusDistribution}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {chartData.statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )} */}

        {/* Category Distribution
        {chartData.categoryDistribution.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Issues by Category</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.categoryDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#92C1FF" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )} */}
      {/* </div> */}
    </div>
  );
};

export default Dashboard;